// Case studies — single source of truth, keyed by slug.
// Accuracy rules: client-reported outcomes only. No fabricated testimonials,
// screenshots, analytics or realised-revenue claims.

export const CASE_STUDIES = {
  "edn-renovation-group": {
    slug: "edn-renovation-group",
    index: "001",
    client: "EDN Renovation Group Ltd",
    industry: "Construction & Renovation",
    date: "3 May 2026",
    services: ["Website Design", "Full-Stack Development", "SEO", "Enquiry System", "Email Automations"],
    website: "ednrenovationgroup.com",
    liveUrl: "https://www.ednrenovationgroup.com/",
    heroMockup: "/work/edn-new.webp",
    showcaseFull: "/work/edn-new-site.webp",
    beforeImage: "/work/edn-old.webp",
    afterImage: "/work/edn-new-site.webp",

    seoTitle: "EDN Renovation Group Case Study | Jay Alminshawi",
    seoDescription:
      "Discover how we redesigned EDN Renovation Group's website with a conversion-focused enquiry system and automations, supporting a £30,000 project booking.",

    hero: {
      eyebrow: "Web Design & Development / Case Study 001",
      headline: "From an Underperforming Website to a £30,000 Project Booking.",
      description:
        "How we transformed EDN Renovation Group's digital presence with a conversion-focused website, an integrated enquiry system and automated customer communications — helping the business attract better-qualified leads and streamline its sales process.",
    },

    problem: {
      number: "01 / The Problem",
      headline: "A Website That Looked Presentable, But Wasn't Built to Convert.",
      intro:
        "Before the redevelopment, EDN Renovation Group was operating with an AI-generated website template that lacked the functionality, strategic content structure and professional presentation needed to support the business.",
      points: [
        {
          title: "Lack of Credibility",
          body: "The visual design, typography, spacing and content presentation did not effectively communicate the company's professionalism or quality of workmanship.",
        },
        {
          title: "Ineffective Customer Journey",
          body: "The website lacked a clear structure guiding visitors from discovering the company's services towards making an enquiry. Calls to action were not positioned effectively throughout the page.",
        },
        {
          title: "Non-Functional Enquiry Infrastructure",
          body: "The existing website did not have a properly functioning enquiry system supported by an active backend database.",
        },
        {
          title: "Lost Enquiry Opportunities",
          body: "Visitors were reportedly leaving the website quickly, with the previous setup failing to generate enquiries through the intended forms.",
        },
      ],
    },

    transformation: {
      number: "02 / The Transformation",
      headline: "Rebuilding the Website Around Trust, Conversion and Efficiency.",
      intro:
        "We rebuilt EDN Renovation Group's website with a clear objective: create a professional digital platform that builds credibility, encourages qualified enquiries and reduces the manual work required to manage potential customers. The transformation combined high-end design, strategic content positioning, backend development and automated communications.",
      features: [
        {
          k: "Feature 01",
          title: "Conversion-Focused Website Design",
          body: "We developed a fully optimised landing page featuring professional typography, improved spacing, structured headings and carefully positioned calls to action. Each section was designed to build visitor confidence and guide potential customers naturally towards submitting an enquiry.",
          benefit: "A more credible digital presence and a clearer path from website visit to enquiry.",
        },
        {
          k: "Feature 02",
          title: "Intelligent Enquiry Collection",
          body: "We developed a fully functional enquiry form connected to a backend database. The form collects the essential project information EDN needs to assess an enquiry and arrange a site visit.",
          benefit: "Better-qualified initial enquiries, less back-and-forth communication and faster progression towards site visits.",
        },
        {
          k: "Feature 03",
          title: "Automated Email Communications",
          body: "We integrated automated email notifications for both the business owner and the customer. When an enquiry is submitted, the project information is recorded, the owner receives a notification and the customer receives an automated confirmation — so EDN can review and follow up promptly.",
          benefit: "Faster communication, a more professional customer experience and reduced risk of enquiries being overlooked.",
        },
        {
          k: "Feature 04",
          title: "Search Engine Optimisation",
          body: "We improved the website's on-page SEO, keyword targeting and underlying website structure to strengthen its search-engine foundations.",
          benefit: "Improved technical and content foundations for organic search visibility.",
        },
      ],
      workflow: [
        { step: "Enquiry submitted", detail: "Homeowner completes the project enquiry form." },
        { step: "Recorded to database", detail: "Project details stored securely and accessibly." },
        { step: "Owner notified", detail: "EDN receives an instant enquiry notification." },
        { step: "Customer confirmed", detail: "Automated confirmation reaches the customer." },
      ],
    },

    outcome: {
      number: "03 / The Commercial Outcome",
      headline: "A £30,000 Project Booking. A More Efficient Enquiry Process.",
      stats: [
        { value: 30000, prefix: "£", display: "£30,000", label: "Value of an additional project booked.", dominant: true },
        { value: 4, display: "4", label: "High-quality enquiries reported." },
        { value: 25, suffix: "%", display: "25%", label: "Lead-to-booking conversion across the four reported enquiries." },
        { value: 6, suffix: " Days", display: "6 Days", label: "Between the recorded enquiry and the project booking." },
      ],
      narrative:
        "Following the website redevelopment, EDN Renovation Group reported four high-quality enquiries, with one progressing into an additional £30,000 project booking. The integrated enquiry form helped collect relevant project information upfront, while automated notifications improved the speed and consistency of customer communications. EDN also reported saving time on administration and unnecessary back-and-forth conversations through the new process.",
      disclaimer:
        "Figures are client-reported outcomes. The 25% figure represents one booking from four reported high-quality enquiries — not a website visitor-to-lead conversion rate. We do not claim the website was the sole cause of the booking, nor that the £30,000 has been realised as revenue.",
      timeline: [
        { date: "15 Oct 2026", label: "High-value project enquiry received." },
        { date: "21 Oct 2026", label: "£30,000 project booking confirmed." },
      ],
    },

    comparison: {
      headline: "The Difference Between Having a Website and Having a System.",
      before: [
        "AI-generated template",
        "Weak visual credibility",
        "Poorly structured customer journey",
        "Non-functional enquiry infrastructure",
        "No effective automated enquiry process",
      ],
      after: [
        "Professionally designed website",
        "Strategically positioned content and CTAs",
        "Fully functional backend enquiry form",
        "Stored and accessible enquiry information",
        "Automated customer and owner notifications",
        "More efficient lead qualification",
      ],
    },

    showcase: {
      headline: "Built to Represent the Business. Designed to Support Growth.",
      body: "The finished EDN Renovation Group website — a calm, editorial renovation brand with a clear enquiry journey across desktop and mobile.",
    },

    cta: {
      eyebrow: "Your website should work harder for your business.",
      headline: "Your Next High-Value Project Could Start With a Better Website.",
      body: "We design and develop high-performance websites, intelligent quotation systems and automated enquiry processes for construction companies looking to attract better-qualified leads and streamline their operations.",
    },
  },
};

export const getCaseStudy = (slug) => CASE_STUDIES[slug] || null;
